import { Theme, useMediaQuery } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api_Routes } from "../../api_Routes.js";
import { Helper } from "../../tools/Helper.js";
import { ChatList } from "./ChatList.tsx";
import ChatWindow from "./ChatWindow.tsx";
import { DesktopWrapper } from "./DesktopWrapper.js";

export const ChatInterface = () => {
  const { type: routeType,user_type:userType, id: routeId } = useParams<{
    type: "individual" | "group" | "channel";
    user_type: "user" | "admin" ;
    id: string;
  }>();
  const [messages, setMessages] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const messagesContainerRef = useRef<HTMLUListElement>(null);
  const [reciverName, setReciverName] = useState(null);
  const [reciverImage, setReciverImage] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState<
    number | null
  >(null);
  const [chatType, setChatType] = useState<"individual" | "group" | "channel">(
    "individual"
  );
  const [conversations, setConversations] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const userString = localStorage.getItem("user");
  let currentUser = { id: 1, name: "" };
  if (userString) {
    const user = JSON.parse(userString);
    currentUser = { id: user.user_id, name: user.name };
  }
  // const getEffectiveChatType = () => {
  //   return routeType || chatType;
  // };

  const maintainScrollPosition = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const prevScrollHeight = container.scrollHeight;
    requestAnimationFrame(() => {
      if (container) {
        container.scrollTop = container.scrollHeight - prevScrollHeight;
      }
    });
  };

  // Function to load initial messages when switching conversations
  const loadInitialMessages = async () => {
    const targetId = routeId || selectedConversation;
    if (!targetId) return;
    // const effectiveChatType = getEffectiveChatType();
    // console.log(
    //   "🔄 Loading initial messages for:",
    //   targetId,
    //   "with type:",
    //   chatType
    // );

    setIsLoadingMore(true);
    setMessages([]);
    setCurrentPage(0);
    setHasMore(true);

    try {
      let url =
        chatType === "individual"
          ? api_Routes.UserChat.Start
          : chatType === "group"
          ? api_Routes.UserChat.GroupMessages
          : api_Routes.UserChat.ChannelMessages;
      url +=
        chatType === "individual"
          ? `?user_id=${targetId}&type=${userType}`
          : `?conversation_id=${targetId}`;

      url += `&page=1`;

      // console.log("📡 Fetching messages from:", url);

      const { response } = await Helper.Get({
        url,
        hasToken: true,
      });

      if (response?.data?.conversation_id) {
        console.log(response?.data?.conversation_id)
        setSelectedConversation(response?.data?.conversation_id);
      }
      if (response?.data?.messages?.length) {
        setCurrentPage(1);
        setMessages(response.data.messages);
        // Set the conversation ID if it's a new conversation
        if (response?.data?.conversation_id) {
          setSelectedConversation(response?.data?.conversation_id);
        }
      }

    } catch (error) {
      console.error("❌ Error fetching initial messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  useEffect(() => {
    // console.log("🔄 useEffect triggered - conversation or chat type changed:", {
    //   selectedConversation,
    //   chatType,
    //   routeId,
    // });

    if (selectedConversation && conversations && chatType === 'individual') {
      const converstion = conversations.find((conv) => conv.id === selectedConversation);
      setReciverName(converstion?.name)
      setReciverImage(converstion?.image)
    }
  }, [selectedConversation, conversations,chatType]);

  // Function to load more messages (for pagination)
  const fetchMessages = async () => {
    if (!hasMore || isLoadingMore) return;
    // const effectiveChatType = getEffectiveChatType();
    setIsLoadingMore(true);
    try {
      let url =
        chatType === "individual"
          ? api_Routes.UserChat.Start
          : chatType === "group"
          ? api_Routes.UserChat.GroupMessages
          : api_Routes.UserChat.ChannelMessages;
      if (routeId) {
        url += `?user_id=${routeId}`;
      } else {
        url += `?conversation_id=${selectedConversation}`;
      }
      const nextPage = currentPage + 1;
      url += `&page=${nextPage}`;

      const { response, message } = await Helper.Get({
        url,
        hasToken: true,
      });

      if (response?.data?.messages?.length) {
        setCurrentPage(nextPage);
        setMessages((prev) => [...response.data.messages, ...prev]);
        maintainScrollPosition();

        // Check if there are more messages based on pagination info or message count
        const messageCount = response.data.messages.length;
        const hasPagination = response.data.pagination;
        if (hasPagination) {
          setHasMore(hasPagination.current_page < hasPagination.last_page);
        } else {
          // Fallback: assume 20 messages per page
          setHasMore(messageCount >= 20);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Load initial messages when conversation or chat type changes
  useEffect(() => {
    // console.log("🔄 useEffect triggered - conversation or chat type changed:", {
    //   selectedConversation,
    //   chatType,
    //   routeId,
    // });

    if (selectedConversation || routeId) {
      loadInitialMessages();
    }
  }, [selectedConversation, chatType, routeId]);

  const handleMarkAsRead = (
    id: number,
    type: "individual" | "group" | "channel"
  ) => {
    // Optimistically update the unread count
    if (type === "individual") {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === id ? { ...conv, unread_count: 0 } : conv
        )
      );
    } else if (type === "group") {
      setGroups((prev) =>
        prev.map((group) =>
          group.id === id ? { ...group, unread_count: 0 } : group
        )
      );
    } else {
      setChannels((prev) =>
        prev.map((channel) =>
          channel.id === id ? { ...channel, unread_count: 0 } : channel
        )
      );
    }

    // Call the API in the background without affecting the UI
    const url =
      type === "individual"
        ? `${api_Routes.UserChat.View}/mark-as-read/${id}`
        : type === "group"
        ? `${api_Routes.UserChat.Group}/mark-as-read/${id}`
        : `${api_Routes.UserChat.Channel}/mark-as-read/${id}`;

    Helper.Post({
      url,
      hasToken: true,
      data: { id } as any,
    }).catch((error) => {
      console.error("Error marking messages as read:", error);
      // Optionally revert the optimistic update if the API call fails
      if (type === "individual") {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === id ? { ...conv, unread_count: conv.unread_count } : conv
          )
        );
      } else if (type === "group") {
        setGroups((prev) =>
          prev.map((group) =>
            group.id === id
              ? { ...group, unread_count: group.unread_count }
              : group
          )
        );
      } else {
        setChannels((prev) =>
          prev.map((channel) =>
            channel.id === id
              ? { ...channel, unread_count: channel.unread_count }
              : channel
          )
        );
      }
    });
  };

  useEffect(() => {
    if (
      routeType === "individual" ||
      routeType === "group" ||
      routeType === "channel"
    ) {
      setChatType(routeType);
    }
  }, [routeType]);
  useEffect(() => {
    const fetchConversations = async () => {
      setIsLoadingChats(true);
      setCurrentPage(0);
      try {
        // Fetch individual chats
        const { response: individualResponse } = await Helper.Get({
          url: api_Routes.UserChat.View,
          hasToken: true,
        });
        if (individualResponse) setConversations(individualResponse.data);

        // Fetch group chats
        const { response: groupResponse } = await Helper.Get({
          url: api_Routes.UserChat.Group,
          hasToken: true,
        });
        if (groupResponse) setGroups(groupResponse.data);

        // Fetch channel chats
        const { response: channelsResponse } = await Helper.Get({
          url: api_Routes.UserChat.Channel,
          hasToken: true,
        });
        if (channelsResponse) setChannels(channelsResponse.data);
      } catch (error) {
        console.error("Error fetching conversations:", error);
      } finally {
        setIsLoadingChats(false);
      }
    };

    fetchConversations();
  }, []);
  return (
    <DesktopWrapper>
      {!isMobile && (
        <ChatList
          conversations={conversations}
          reciverName={reciverName}
          setReciverName={setReciverName}
          reciverImage={reciverImage}
          setReciverImage={setReciverImage}
          groups={groups}
          setSelectedConversation={setSelectedConversation}
          selectedConversation={
            selectedConversation || parseInt(routeId || "0")
          }
          setChatType={setChatType}
          chatType={chatType}
          isLoading={isLoadingChats}
          onMarkAsRead={handleMarkAsRead}
          setConversations={setConversations}
          setGroups={setGroups}
          setChannels={setChannels}
          channels={channels}
        />
      )}

      {isMobile ? (
        routeId || selectedConversation || routeId ? (
          <ChatWindow
              reciverName={reciverName}
              reciverImage={reciverImage}
            setSelectedConversation={setSelectedConversation}
            selectedConversation={
              selectedConversation || parseInt(routeId || "0")
            }
            messages={messages}
            currentUser={currentUser}
            loadMoreMessages={fetchMessages}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            messagesContainerRef={messagesContainerRef}
            maintainScrollPosition={maintainScrollPosition}
            setMessages={setMessages}
            chatType={chatType}
            isLoadingChats={isLoadingChats}
          />
        ) : (
          <ChatList
            conversations={conversations}
            reciverName={reciverName}
            setReciverName={setReciverName}
            reciverImage={reciverImage}
            setReciverImage={setReciverImage}
            groups={groups}
            setSelectedConversation={setSelectedConversation}
            selectedConversation={
              selectedConversation || parseInt(routeId || "0")
            }
            setChatType={setChatType}
            chatType={chatType}
            isLoading={isLoadingChats}
            onMarkAsRead={handleMarkAsRead}
            setConversations={setConversations}
            setGroups={setGroups}
            setChannels={setChannels}
            channels={channels}
          />
        )
      ) : (
        <ChatWindow
            reciverName={reciverName}
            reciverImage={reciverImage}
          setSelectedConversation={setSelectedConversation}
          selectedConversation={
            selectedConversation || parseInt(routeId || "0")
          }
          messages={messages}
          currentUser={currentUser}
          loadMoreMessages={fetchMessages}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          messagesContainerRef={messagesContainerRef}
          maintainScrollPosition={maintainScrollPosition}
          setMessages={setMessages}
          chatType={chatType}
          isLoadingChats={isLoadingChats}
        />
      )}
    </DesktopWrapper>
  );
};
